import { DateTimeUtils } from '@servicetitan/datetime-utils';
import {
    AppointmentState,
    AppointmentTimeOptionStatus,
    AppointmentTimeOptionsSettings,
} from './appointment-window';

export const validateDate = (dateToValidate?: Date) => {
    if (dateToValidate && dateToValidate.getFullYear() < 1900) {
        return undefined;
    }
    return dateToValidate;
};

const browserTimeZone = (): string | undefined => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
        return undefined;
    }
};

/**
 * Short zone name for this instant in the browser zone (timeZoneName: 'short'),
 * for example "MST". The clock time itself stays on formatShortTime.
 */
const zoneAbbreviation = (dateTimeUtils: DateTimeUtils, date: Date): string | undefined => {
    const timeZone = browserTimeZone();
    if (!timeZone) {
        return undefined;
    }

    try {
        return new Intl.DateTimeFormat(dateTimeUtils.getLocale(), {
            timeZone,
            timeZoneName: 'short',
        })
            .formatToParts(date)
            .find(part => part.type === 'timeZoneName')?.value;
    } catch {
        return undefined;
    }
};

const formatAppointmentTime = (dateTimeUtils: DateTimeUtils, date: Date) => {
    const time = dateTimeUtils.formatShortTime(date);
    const abbreviation = zoneAbbreviation(dateTimeUtils, date);
    return abbreviation ? `${time} ${abbreviation}` : time;
};

export const getDateText = (dateTimeUtils: DateTimeUtils, date?: Date) => {
    const validatedDate = validateDate(date);
    if (!validatedDate) {
        return 'Date unavailable';
    }
    return validatedDate
        ? `${dateTimeUtils.formatShortMonth(validatedDate)} ${validatedDate.getDate()}, ${validatedDate.getFullYear()}`
        : 'Date unavailable';
};

export const getWindowText = (dateTimeUtils: DateTimeUtils, start: Date, end?: Date) => {
    const part1 = formatAppointmentTime(dateTimeUtils, start);
    const part2 = end ? ` - ${formatAppointmentTime(dateTimeUtils, end)}` : '';
    return `${part1}${part2}`;
};

export const getAppointmentWindowText = (
    dateTimeUtils: DateTimeUtils,
    startDate?: Date,
    endDate?: Date
): string | undefined => {
    const validatedStartDate = validateDate(startDate);
    const validatedEndDate = validateDate(endDate);
    if (!validatedStartDate && !validatedEndDate) {
        return 'Date unavailable';
    }

    return validatedStartDate
        ? `${getDateText(dateTimeUtils, validatedStartDate)} • ${getWindowText(dateTimeUtils, validatedStartDate, validatedEndDate)}`
        : undefined;
};

export const getDisplayOption = (
    appointmentState: AppointmentState,
    appointmentTimeOptions?: AppointmentTimeOptionsSettings
): AppointmentTimeOptionStatus => {
    if (!appointmentTimeOptions) {
        return AppointmentTimeOptionStatus.StartAndEndTime;
    }

    switch (appointmentState) {
        case AppointmentState.Scheduled:
            return appointmentTimeOptions.scheduled.selection;
        case AppointmentState.InProgress:
            return appointmentTimeOptions.inProgress.selection;
        case AppointmentState.Completed:
            return appointmentTimeOptions.completed.selection;
        case AppointmentState.CanceledOrOnHold:
            return AppointmentTimeOptionStatus.DateOnly;
        default:
            return AppointmentTimeOptionStatus.StartAndEndTime;
    }
};
